"use client";

import { useEffect, useMemo, useState } from "react";

type Layer = "skin" | "bsf";

type AtlasData = {
  skin: {
    ids: string[];
    timepoints: string[];
    timepointLabels: Record<string, string>;
    timepointByCell: string[];
    sampleCounts: Record<string, number>;
    unit: string;
    featureCount: number;
  };
  bsf: {
    ids: string[];
    unit: string;
    featureCount: number;
  };
  excluded: string[];
  source: {
    layoutNote: string;
    expressionNote: string;
    repositoryUrl: string;
  };
};

type GeneRecord = {
  id: string;
  name: string | null;
  product: string | null;
  currentRecord: boolean;
  inSkin: boolean;
  inBsf: boolean;
};

type GeneIndex = {
  genes: GeneRecord[];
  featureCount: number;
  skinFeatureCount: number;
  bsfFeatureCount: number;
  annotatedFeatures: number;
};

type SparseGene = {
  skin: number[] | null;
  bsf: number[];
};

function fnvBucket(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return String((hash >>> 0) % 64).padStart(2, "0");
}

function decodeSparse(sparse: number[] | null, length: number) {
  if (!sparse) return null;
  const values = new Array(length).fill(0) as number[];
  for (let index = 0; index < sparse.length; index += 2) {
    values[sparse[index]] = sparse[index + 1];
  }
  return values;
}

function quantile(values: number[], probability: number) {
  if (!values.length) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  const index = Math.min(
    sorted.length - 1,
    Math.floor((sorted.length - 1) * probability),
  );
  return sorted[index];
}

function summary(values: number[] | null) {
  if (!values) return null;
  const detected = values.filter((value) => value > 0);
  return {
    detected: detected.length,
    medianNonzero: quantile(detected, 0.5),
    maximum: values.length ? Math.max(...values) : 0,
    colourCeiling: quantile(detected, 0.99) || Math.max(...values, 0),
  };
}

function pointColour(value: number, ceiling: number, hue: Layer) {
  if (value <= 0 || ceiling <= 0) return "#fffef9";
  const ratio = Math.min(1, Math.sqrt(value / ceiling));
  const target = hue === "skin" ? [11, 117, 108] : [210, 92, 63];
  const start = [235, 240, 233];
  return `rgb(${start
    .map((channel, index) =>
      Math.round(channel + (target[index] - channel) * ratio),
    )
    .join(",")})`;
}

function download(name: string, content: string, type: string) {
  const anchor = document.createElement("a");
  anchor.href = URL.createObjectURL(new Blob([content], { type }));
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(anchor.href);
}

const text = {
  zh: {
    search: "搜索11,707个GEO表达特征",
    loading: "正在读取作者存档数据…",
    error: "真实数据文件暂时无法读取；本站未使用替代值。",
    skin: "后循环型与皮肤时间序列",
    bsf: "血流型对照",
    timeline: "真实采样时间轴（不是PCA或UMAP）",
    grid: "真实BSF样本索引（不是PCA或UMAP）",
    selected: "固定细胞信息",
    hover: "悬停或单击任一点；信息卡固定在图外，不遮挡选择。",
    gene: "当前基因",
    unit: "单位",
    expression: "表达值",
    timepoint: "采样条件",
    detected: "检出细胞",
    median: "非零中位数",
    maximum: "原始最大值",
    ceiling: "颜色上限（非零第99百分位）",
    noSkin: "该特征不在8,318行皮肤归一化表中。",
    separate:
      "两层来自不同公开文件、使用不同数值单位，因此分别着色、分别汇总，不能直接比较颜色深浅。",
    downloadGene: "下载当前基因JSON",
    downloadCells: "下载170个细胞元数据CSV",
    annotations:
      "名称和产物只在现有TrypCell基因索引可精确匹配时显示；未匹配记录不推断注释。",
  },
  en: {
    search: "Search 11,707 GEO expression features",
    loading: "Loading author-deposited data…",
    error: "The real data files are unavailable; no substitute values are shown.",
    skin: "Metacyclic and skin time course",
    bsf: "Bloodstream-form controls",
    timeline: "Real sampling timeline (not PCA or UMAP)",
    grid: "Real BSF sample index (not PCA or UMAP)",
    selected: "Pinned cell information",
    hover:
      "Hover or click any point; the information card stays outside the plot.",
    gene: "Current gene",
    unit: "Unit",
    expression: "Expression value",
    timepoint: "Collection condition",
    detected: "Detected cells",
    median: "Non-zero median",
    maximum: "Original maximum",
    ceiling: "Colour ceiling (99th percentile of non-zero values)",
    noSkin:
      "This feature is absent from the 8,318-row normalized skin table.",
    separate:
      "The two layers come from different public files and use different units. They have separate colour scales and summaries; colour depth is not comparable between panels.",
    downloadGene: "Download current-gene JSON",
    downloadCells: "Download 170-cell metadata CSV",
    annotations:
      "Names and products appear only when an exact match exists in a current TrypCell gene index; unmatched records are not inferred.",
  },
};

export function ReuterSkinExplorer() {
  const [language, setLanguage] = useState<"zh" | "en">("zh");
  const [atlas, setAtlas] = useState<AtlasData | null>(null);
  const [geneIndex, setGeneIndex] = useState<GeneIndex | null>(null);
  const [gene, setGene] = useState("Tb927.6.4280");
  const [query, setQuery] = useState("Tb927.6.4280");
  const [expression, setExpression] = useState<{
    gene: string;
    skin: number[] | null;
    bsf: number[];
  } | null>(null);
  const [selected, setSelected] = useState<{ layer: Layer; index: number } | null>(
    null,
  );
  const [hovered, setHovered] = useState<{
    layer: Layer;
    index: number;
  } | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    const updateLanguage = (event: Event) =>
      setLanguage((event as CustomEvent<"zh" | "en">).detail);
    window.addEventListener("trypcell-language", updateLanguage);
    setLanguage(document.body.dataset.language === "en" ? "en" : "zh");
    return () =>
      window.removeEventListener("trypcell-language", updateLanguage);
  }, []);

  useEffect(() => {
    Promise.all([
      fetch("/data/reuter-gse174198-atlas.json").then((response) => {
        if (!response.ok) throw new Error("atlas");
        return response.json() as Promise<AtlasData>;
      }),
      fetch("/data/reuter-gse174198-gene-index.json").then((response) => {
        if (!response.ok) throw new Error("index");
        return response.json() as Promise<GeneIndex>;
      }),
    ])
      .then(([atlasPayload, indexPayload]) => {
        setAtlas(atlasPayload);
        setGeneIndex(indexPayload);
        const requested = new URLSearchParams(window.location.search).get(
          "gene",
        );
        if (
          requested &&
          indexPayload.genes.some((record) => record.id === requested)
        ) {
          setGene(requested);
          setQuery(requested);
        }
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    if (!atlas || !geneIndex) return;
    fetch(`/data/reuter-expression/bucket-${fnvBucket(gene)}.json`)
      .then((response) => {
        if (!response.ok) throw new Error("expression");
        return response.json() as Promise<{ genes: Record<string, SparseGene> }>;
      })
      .then((payload) => {
        const record = payload.genes[gene];
        if (!record) throw new Error("gene");
        setExpression({
          gene,
          skin: decodeSparse(record.skin, atlas.skin.ids.length),
          bsf: decodeSparse(record.bsf, atlas.bsf.ids.length) ?? [],
        });
      })
      .catch(() => setStatus("error"));
  }, [atlas, gene, geneIndex]);

  const t = text[language];
  const displayedExpression = expression?.gene === gene ? expression : null;
  const currentGene = geneIndex?.genes.find((record) => record.id === gene);
  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || !geneIndex) return [];
    return geneIndex.genes
      .filter((record) =>
        [record.id, record.name, record.product]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle),
      )
      .slice(0, 8);
  }, [geneIndex, query]);
  const skinSummary = summary(displayedExpression?.skin ?? null);
  const bsfSummary = summary(displayedExpression?.bsf ?? null);
  const active = hovered ?? selected;
  const activeIds =
    active?.layer === "skin" ? atlas?.skin.ids : atlas?.bsf.ids;
  const activeValues =
    active?.layer === "skin"
      ? displayedExpression?.skin
      : displayedExpression?.bsf;
  const activeId = active && activeIds ? activeIds[active.index] : null;
  const activeValue =
    active && activeValues ? activeValues[active.index] : null;
  const activeTimepoint =
    active?.layer === "skin" && atlas
      ? atlas.skin.timepointByCell[active.index]
      : "BSF";

  const chooseGene = (record: GeneRecord) => {
    setGene(record.id);
    setQuery(record.id);
    setSelected(null);
    const url = new URL(window.location.href);
    url.searchParams.set("gene", record.id);
    window.history.replaceState({}, "", url);
  };

  const downloadGene = () => {
    if (!atlas || !displayedExpression || !currentGene) return;
    download(
      `${gene}-GSE174198.json`,
      `${JSON.stringify(
        {
          study: "Reuter et al. 2023",
          accession: "GSE174198",
          gene: currentGene,
          skin: {
            unit: atlas.skin.unit,
            cells: atlas.skin.ids.map((id, index) => ({
              id,
              timepoint: atlas.skin.timepointByCell[index],
              value: displayedExpression.skin?.[index] ?? null,
            })),
          },
          bsf: {
            unit: atlas.bsf.unit,
            cells: atlas.bsf.ids.map((id, index) => ({
              id,
              value: displayedExpression.bsf[index],
            })),
          },
          boundary: atlas.source.expressionNote,
        },
        null,
        2,
      )}\n`,
      "application/json",
    );
  };

  const downloadCells = () => {
    if (!atlas) return;
    const rows = [
      ["cell_id", "layer", "collection_condition"],
      ...atlas.skin.ids.map((id, index) => [
        id,
        "MCF/skin normalized table",
        atlas.skin.timepointByCell[index],
      ]),
      ...atlas.bsf.ids.map((id) => [id, "BSF processed count table", "BSF"]),
    ];
    download(
      "GSE174198-retained-cell-metadata.csv",
      `${rows.map((row) => row.join(",")).join("\n")}\n`,
      "text/csv",
    );
  };

  if (status === "loading" || !atlas || !geneIndex) {
    return <div className="reuter-loading">{t.loading}</div>;
  }
  if (status === "error") {
    return <div className="reuter-loading error">{t.error}</div>;
  }

  const renderSkinPoints = () => {
    let cursor = 0;
    return atlas.skin.timepoints.flatMap((timepoint, column) => {
      const count = atlas.skin.sampleCounts[timepoint];
      const points = Array.from({ length: count }, (_, row) => {
        const index = cursor + row;
        const value = displayedExpression?.skin?.[index] ?? 0;
        const isActive =
          active?.layer === "skin" && active.index === index;
        return (
          <circle
            key={atlas.skin.ids[index]}
            cx={92 + column * 145}
            cy={74 + row * 15}
            r={isActive ? 6.5 : 4.5}
            fill={pointColour(
              value,
              skinSummary?.colourCeiling ?? 0,
              "skin",
            )}
            stroke={isActive ? "#00a6b2" : "#49645f"}
            strokeWidth={isActive ? 2.8 : 0.8}
            role="button"
            tabIndex={0}
            aria-label={atlas.skin.ids[index]}
            onMouseEnter={() => setHovered({ layer: "skin", index })}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered({ layer: "skin", index })}
            onBlur={() => setHovered(null)}
            onClick={() => setSelected({ layer: "skin", index })}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                setSelected({ layer: "skin", index });
              }
            }}
          />
        );
      });
      cursor += count;
      return points;
    });
  };

  return (
    <div className="reuter-explorer">
      <div className="reuter-gene-tools">
        <div>
          <label htmlFor="reuter-gene-search">{t.search}</label>
          <input
            id="reuter-gene-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query !== gene || suggestions[0]?.id !== gene ? (
            <div className="reuter-suggestions">
              {suggestions.map((record) => (
                <button
                  key={record.id}
                  type="button"
                  onClick={() => chooseGene(record)}
                >
                  <strong>{record.name || record.id}</strong>
                  <span>{record.id}</span>
                  <small>{record.product || "No matched product annotation"}</small>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <article>
          <span>{t.gene}</span>
          <strong>{currentGene?.name || gene}</strong>
          <code>{gene}</code>
          <p>{currentGene?.product || t.annotations}</p>
        </article>
      </div>

      <div className="reuter-unit-warning">
        <strong>SEPARATE EXPRESSION LAYERS</strong>
        <p>{t.separate}</p>
      </div>

      <div className="reuter-plot-layout">
        <div className="reuter-plots">
          <section>
            <header>
              <div>
                <span>{t.skin}</span>
                <h3>{t.timeline}</h3>
              </div>
              <small>{atlas.skin.unit}</small>
            </header>
            {!currentGene?.inSkin ? (
              <p className="reuter-missing-layer">{t.noSkin}</p>
            ) : (
              <svg
                className="reuter-skin-svg"
                viewBox="0 0 760 520"
                role="img"
                aria-label={t.timeline}
              >
                {atlas.skin.timepoints.map((timepoint, column) => (
                  <g key={timepoint}>
                    <text x={92 + column * 145} y="27" textAnchor="middle">
                      {atlas.skin.timepointLabels[timepoint]}
                    </text>
                    <text
                      className="count-label"
                      x={92 + column * 145}
                      y="46"
                      textAnchor="middle"
                    >
                      n={atlas.skin.sampleCounts[timepoint]}
                    </text>
                    <line
                      x1={92 + column * 145}
                      x2={92 + column * 145}
                      y1="58"
                      y2="500"
                    />
                  </g>
                ))}
                {renderSkinPoints()}
              </svg>
            )}
            {skinSummary ? (
              <div className="reuter-summary-row">
                <span>
                  {t.detected} <strong>{skinSummary.detected} / 139</strong>
                </span>
                <span>
                  {t.median} <strong>{skinSummary.medianNonzero.toPrecision(4)}</strong>
                </span>
                <span>
                  {t.maximum} <strong>{skinSummary.maximum.toPrecision(5)}</strong>
                </span>
                <span>
                  {t.ceiling}{" "}
                  <strong>{skinSummary.colourCeiling.toPrecision(5)}</strong>
                </span>
              </div>
            ) : null}
          </section>

          <section>
            <header>
              <div>
                <span>{t.bsf}</span>
                <h3>{t.grid}</h3>
              </div>
              <small>{atlas.bsf.unit}</small>
            </header>
            <div className="reuter-bsf-grid">
              {atlas.bsf.ids.map((id, index) => {
                const value = displayedExpression?.bsf[index] ?? 0;
                const isActive =
                  active?.layer === "bsf" && active.index === index;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-label={id}
                    style={{
                      background: pointColour(
                        value,
                        bsfSummary?.colourCeiling ?? 0,
                        "bsf",
                      ),
                    }}
                    className={isActive ? "active" : ""}
                    onMouseEnter={() => setHovered({ layer: "bsf", index })}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered({ layer: "bsf", index })}
                    onBlur={() => setHovered(null)}
                    onClick={() => setSelected({ layer: "bsf", index })}
                  >
                    <span />
                  </button>
                );
              })}
            </div>
            {bsfSummary ? (
              <div className="reuter-summary-row">
                <span>
                  {t.detected} <strong>{bsfSummary.detected} / 31</strong>
                </span>
                <span>
                  {t.median} <strong>{bsfSummary.medianNonzero.toPrecision(4)}</strong>
                </span>
                <span>
                  {t.maximum} <strong>{bsfSummary.maximum.toLocaleString()}</strong>
                </span>
                <span>
                  {t.ceiling}{" "}
                  <strong>{bsfSummary.colourCeiling.toLocaleString()}</strong>
                </span>
              </div>
            ) : null}
          </section>
        </div>

        <aside className="reuter-cell-card">
          <span>{t.selected}</span>
          <h3>{activeId || "—"}</h3>
          <p>{t.hover}</p>
          <dl>
            <div>
              <dt>{t.timepoint}</dt>
              <dd>
                {activeTimepoint === "BSF"
                  ? "BSF"
                  : atlas.skin.timepointLabels[activeTimepoint] || "—"}
              </dd>
            </div>
            <div>
              <dt>{t.unit}</dt>
              <dd>
                {active?.layer === "skin"
                  ? atlas.skin.unit
                  : active?.layer === "bsf"
                    ? atlas.bsf.unit
                    : "—"}
              </dd>
            </div>
            <div>
              <dt>{t.expression}</dt>
              <dd>
                {typeof activeValue === "number"
                  ? activeValue.toLocaleString(undefined, {
                      maximumSignificantDigits: 7,
                    })
                  : "—"}
              </dd>
            </div>
          </dl>
          <div className="reuter-downloads">
            <button type="button" onClick={downloadGene}>
              {t.downloadGene}
            </button>
            <button type="button" onClick={downloadCells}>
              {t.downloadCells}
            </button>
          </div>
        </aside>
      </div>
      <p className="reuter-layout-note">{atlas.source.layoutNote}</p>
    </div>
  );
}
